/**
 * useNotifications — WebSocket hook for proactive gateway notifications.
 *
 * Opens a persistent WS connection to /ws/notifications, reconnects
 * on drop, and exposes incoming notifications + a dismiss function.
 */

import { useEffect, useRef, useState, useCallback } from "react";

export interface EmailNotificationData {
  draftId: string;
  draftBody: string;
  from: string;
  senderName: string;
  subject: string;
  messageId: string;
}

export interface ProactiveNotification {
  id: string; // run_id from the backend
  jobId: string;
  jobName: string;
  text: string;
  tenantId: string;
  userId: string;
  receivedAt: number; // Date.now()
  dismissed: boolean;
  emailData?: EmailNotificationData;
  reconfirmRequired?: boolean;
  lifecycleState?: string;
}

export interface MemoryEvent {
  id: string;
  event_type: string; // ltm_promoted | candidate_created | candidate_bump | contradiction_detected | ...
  label: string;      // human-readable label from backend
  value: string;
  category: string;
  confidence: number | null;
  detail: string;
  receivedAt: number;
  dismissed: boolean;
}

interface UseNotificationsOpts {
  tenantId?: string;
  userId?: string;
  /** Called when a new notification arrives */
  onNotification?: (n: ProactiveNotification) => void;
  /** Called when a memory activity event arrives */
  onMemoryEvent?: (e: MemoryEvent) => void;
}

const RECONNECT_DELAY_MS = 3_000;
const MAX_NOTIFICATIONS = 50;
const MAX_MEMORY_EVENTS = 100;

export function useNotifications({
  tenantId = "default",
  userId = "default_user",
  onNotification,
  onMemoryEvent,
}: UseNotificationsOpts = {}) {
  const [notifications, setNotifications] = useState<ProactiveNotification[]>(
    [],
  );
  const [memoryEvents, setMemoryEvents] = useState<MemoryEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;
  const onMemoryEventRef = useRef(onMemoryEvent);
  onMemoryEventRef.current = onMemoryEvent;

  // Dedup guard — prevents duplicate notifications from StrictMode double-mount
  const seenIds = useRef<Set<string>>(new Set());
  const unmountedRef = useRef(false);

  // Stable identity refs for tenant/user to avoid reconnect loops
  const tenantRef = useRef(tenantId);
  const userRef = useRef(userId);
  tenantRef.current = tenantId;
  userRef.current = userId;

  const connect = useCallback(() => {
    // Build WS URL — works behind Vite proxy (dev) and same-origin (prod)
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/ws/notifications`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      // Send identification frame
      ws.send(
        JSON.stringify({
          tenant_id: tenantRef.current,
          user_id: userRef.current,
        }),
      );
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Memory activity events (debug feed)
        if (data.type === "memory_event") {
          const me: MemoryEvent = {
            id: crypto.randomUUID(),
            event_type: data.event_type || "",
            label: data.label || data.event_type || "memory",
            value: data.value || "",
            category: data.category || "",
            confidence: data.confidence ?? null,
            detail: data.detail || "",
            receivedAt: Date.now(),
            dismissed: false,
          };
          setMemoryEvents((prev) => [me, ...prev].slice(0, MAX_MEMORY_EVENTS));
          onMemoryEventRef.current?.(me);
          return;
        }

        if (
          data.type !== "proactive_notification" &&
          data.type !== "email_draft_pending"
        )
          return;

        const nId: string = data.run_id || data.draft_id || crypto.randomUUID();

        // Skip duplicates (e.g. from StrictMode double-mount)
        if (seenIds.current.has(nId)) return;
        seenIds.current.add(nId);

        const notification: ProactiveNotification = {
          id: nId,
          jobId: data.job_id || data.draft_id || "",
          jobName:
            data.job_name ||
            (data.type === "email_draft_pending" ? "Email Draft" : ""),
          text:
            data.text ||
            (data.type === "email_draft_pending"
              ? `Draft reply ready for: ${data.subject || data.from || "new email"}`
              : ""),
          tenantId: data.tenant_id,
          userId: data.user_id,
          receivedAt: Date.now(),
          dismissed: false,
          emailData:
            data.type === "email_draft_pending"
              ? {
                  draftId: data.draft_id || "",
                  draftBody: data.draft_body || "",
                  from: data.from || "",
                  senderName: data.sender_name || "",
                  subject: data.subject || "",
                  messageId: data.original_message_id || "",
                }
              : undefined,
          reconfirmRequired: Boolean(data.reconfirm_required),
          lifecycleState: data.lifecycle_state || undefined,
        };

        setNotifications((prev) =>
          [notification, ...prev].slice(0, MAX_NOTIFICATIONS),
        );

        // Fire callback
        onNotificationRef.current?.(notification);
      } catch {
        // ignore non-JSON or unexpected frames
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Only reconnect if component is still mounted
      if (!unmountedRef.current) {
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  // Connect on mount, reconnect on tenant/user change, disconnect on unmount.
  // Previously this had only [connect] in deps. `connect` is stabilised
  // with [] deps and reads tenantRef/userRef via closure, so the WS never
  // reconnected when tenant/user props changed — meaning if a user ever
  // switched orgs mid-session the socket would silently keep delivering
  // notifications scoped to the OLD tenant. Now we close + re-establish
  // when those props move.
  useEffect(() => {
    unmountedRef.current = false;
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    wsRef.current?.close();
    connect();
    return () => {
      unmountedRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [tenantId, userId, connect]);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, dismissed: true } : n)),
    );
  }, []);

  const dismissAll = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, dismissed: true })));
  }, []);

  const dismissMemoryEvent = useCallback((id: string) => {
    setMemoryEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, dismissed: true } : e)),
    );
  }, []);

  const dismissAllMemoryEvents = useCallback(() => {
    setMemoryEvents((prev) => prev.map((e) => ({ ...e, dismissed: true })));
  }, []);

  const unread = notifications.filter((n) => !n.dismissed);
  const activeMemoryEvents = memoryEvents.filter((e) => !e.dismissed);

  return {
    notifications,
    unread,
    connected,
    dismiss,
    dismissAll,
    memoryEvents,
    activeMemoryEvents,
    dismissMemoryEvent,
    dismissAllMemoryEvents,
  };
}
